'use client'

/**
 * FloatingTooltip — Cursor-Following Variante (2026-06-18 abends, Stephan-Spec)
 *
 * Tooltip folgt der Maus mit Spring-Dampfung — eleganter Award-Pattern (Linear,
 * Vercel, Stripe). Aktiviert sich bei Elementen mit [data-tip] nach kurzer
 * Hover-Verzögerung. Folgt dann der Cursor-Position, clamped am Viewport-Rand,
 * flippt automatisch nach oben/links wenn nicht genug Platz unten/rechts.
 *
 *  - framer-motion useMotionValue + useSpring für die x/y-Animation
 *  - pointer-events: none → blockiert keine Klicks
 *  - prefers-reduced-motion → snap statt spring
 *  - Backdrop-Blur + Brand-Border (Gold-Akzent)
 *  - Theme-aware
 *
 * Inhalt: <FloatingTooltip /> einmal im Root-Layout — fertig. Komponenten
 * setzen einfach `data-tip="…"`.
 */

import { useEffect, useRef, useState } from 'react'
import { motion, AnimatePresence, useMotionValue, useSpring } from 'framer-motion'

const HOVER_DELAY_MS = 280       // Schneller als 350ms damit der Folge-Effekt früher sichtbar wird
const CURSOR_OFFSET_X = 16       // Tooltip standardmäßig 16px rechts vom Cursor
const CURSOR_OFFSET_Y = 16       // und 16px unter dem Cursor
const VIEWPORT_PAD = 8
const MAX_WIDTH = 340

function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false
  } catch { return false }
}

export function FloatingTooltip() {
  const tipRef = useRef<HTMLDivElement>(null)
  const [text, setText] = useState('')
  const [visible, setVisible] = useState(false)

  // Cursor-Tracking via framer-motion MotionValues (re-render-frei)
  const cursorX = useMotionValue(-9999)
  const cursorY = useMotionValue(-9999)
  const reduced = useRef(false)
  // Spring-Dampfung: stiffness/damping abgestimmt auf "elegant aber nicht träge"
  const x = useSpring(cursorX, { stiffness: 420, damping: 32, mass: 0.45 })
  const y = useSpring(cursorY, { stiffness: 420, damping: 32, mass: 0.45 })

  const hoverTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentTarget = useRef<HTMLElement | null>(null)
  const visibleRef = useRef(false)
  useEffect(() => { visibleRef.current = visible }, [visible])

  useEffect(() => {
    reduced.current = prefersReducedMotion()

    function clampPosition(rawX: number, rawY: number) {
      const tip = tipRef.current
      const tipW = tip?.offsetWidth || 220
      const tipH = tip?.offsetHeight || 40
      const vw = document.documentElement.clientWidth
      const vh = window.innerHeight

      let nx = rawX + CURSOR_OFFSET_X
      let ny = rawY + CURSOR_OFFSET_Y

      // Flip horizontal wenn rechts kein Platz → links vom Cursor
      if (nx + tipW + VIEWPORT_PAD > vw) {
        nx = rawX - tipW - CURSOR_OFFSET_X
      }
      // Flip vertikal wenn unten kein Platz → über dem Cursor
      if (ny + tipH + VIEWPORT_PAD > vh) {
        ny = rawY - tipH - CURSOR_OFFSET_Y
      }
      // Clamp links/oben
      if (nx < VIEWPORT_PAD) nx = VIEWPORT_PAD
      if (ny < VIEWPORT_PAD) ny = VIEWPORT_PAD

      return { nx, ny }
    }

    function onMouseMove(e: MouseEvent) {
      // Position immer mitschreiben (auch wenn Tooltip noch nicht sichtbar) —
      // dann hat das Spring beim Erscheinen schon den richtigen Startpunkt.
      const { nx, ny } = clampPosition(e.clientX, e.clientY)
      if (reduced.current) {
        // Snap statt Spring: setzt direkt die Spring-Werte gleich auf Ziel
        cursorX.set(nx)
        cursorY.set(ny)
      } else {
        cursorX.set(nx)
        cursorY.set(ny)
      }
    }

    function onOver(e: MouseEvent) {
      const target = (e.target as HTMLElement)?.closest?.('[data-tip]') as HTMLElement | null
      if (!target) return
      const tip = target.getAttribute('data-tip')
      if (!tip) return

      // Wenn schon dasselbe Target → nichts tun
      if (currentTarget.current === target && visibleRef.current) return

      if (hoverTimer.current) clearTimeout(hoverTimer.current)
      currentTarget.current = target
      hoverTimer.current = setTimeout(() => {
        setText(tip)
        setVisible(true)
        // Beim Erscheinen Position direkt am Cursor (kein Spring vom letzten Punkt)
        if (reduced.current) {
          // bereits per onMouseMove gesetzt
        }
      }, HOVER_DELAY_MS)
    }

    function onOut(e: MouseEvent) {
      const related = (e.relatedTarget as HTMLElement | null)
      const left = (e.target as HTMLElement)?.closest?.('[data-tip]')
      if (!left) return
      const relatedTip = related?.closest?.('[data-tip]')
      if (relatedTip === left) return
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
      currentTarget.current = null
      setVisible(false)
    }

    function onScroll() {
      // Beim Scrollen Tooltip schließen — Element-Bezug wäre sonst inkonsistent
      if (visibleRef.current) {
        if (hoverTimer.current) clearTimeout(hoverTimer.current)
        currentTarget.current = null
        setVisible(false)
      }
    }

    document.addEventListener('mousemove', onMouseMove, { passive: true })
    document.addEventListener('mouseover', onOver, { passive: true })
    document.addEventListener('mouseout', onOut, { passive: true })
    window.addEventListener('scroll', onScroll, { passive: true, capture: true })

    return () => {
      if (hoverTimer.current) clearTimeout(hoverTimer.current)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseover', onOver)
      document.removeEventListener('mouseout', onOut)
      window.removeEventListener('scroll', onScroll, { capture: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <AnimatePresence>
      {visible && text && (
        <motion.div
          ref={tipRef}
          initial={{ opacity: 0, scale: 0.92 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.94 }}
          transition={{ type: 'spring', stiffness: 460, damping: 30, mass: 0.4 }}
          style={{
            position: 'fixed',
            left: 0,
            top: 0,
            x,   // ← Spring-getriebene MotionValues, folgen dem Cursor
            y,
            zIndex: 99999,
            pointerEvents: 'none',
            maxWidth: MAX_WIDTH,
            padding: '8px 12px',
            borderRadius: 12,
            fontSize: 12,
            lineHeight: 1.45,
            fontWeight: 600,
            letterSpacing: '.01em',
            color: 'var(--text, #111827)',
            background: 'var(--bg-card, #ffffff)',
            border: '1.5px solid color-mix(in oklch, var(--accent, #8a5300) 45%, transparent)', // 2026-06-19 Kontrast-Sweep: Fallback #d99b1c → #8a5300 (CB-safe)
            boxShadow: '0 12px 36px rgba(0,0,0,.40), 0 0 0 1px rgba(0,0,0,.02)',
            backdropFilter: 'blur(14px)',
            WebkitBackdropFilter: 'blur(14px)',
            whiteSpace: text.includes('\n') ? 'pre-line' : 'normal',
            textAlign: text.includes('\n') ? 'left' : 'center',
            wordBreak: 'normal',
            overflowWrap: 'normal',
          }}
        >
          {text}
        </motion.div>
      )}
    </AnimatePresence>
  )
}
